import { IconName } from '@travelata-libs/ui-kit';
import {
  BeachLine,
  BeachType,
  ChildService,
  DistanceLift,
  HotelFacility,
  RenovationType,
  RestType,
  WaterActivity,
} from '@/entities/hotel';
import { HotelCategoryId } from '@/entities/hotelCategory';
import { MealId } from '@/entities/meal';
import { FilterGroupId, type SerpFilterGroup, type SerpQuickFilter } from '../model/types';
import BudgetFilter from '../ui/BudgetFilter.vue';
import EmiratesFilter from '../ui/EmiratesFilter.vue';
import OperatorsFilter from '../ui/OperatorsFilter.vue';
import RatingFilter from '../ui/RatingFilter.vue';
import ResortsFilter from '../ui/ResortsFilter.vue';

export const BUDGET_FILTER_STEP = 10_000;

export const BUDGET_FILTER_PRESETS: readonly number[] = [
  25_000, 50_000, 75_000, 100_000, 150_000, 200_000, 250_000, 350_000, 500_000, 650_000, 800_000, 1_000_000, 1_500_000,
  3_000_000, 6_000_000,
];

export const BUDGET_FILTER_PRESETS_LIMIT = 6;

export const BUDGET_FILTER_COMMIT_DELAY = 500;

export const SERP_FILTERS_RESET_TOAST_DURATION_MS = 10000;

const IS_EXCLUSIVE_FILTER_TITLE = 'Фиксированная цена от Travelata';

const BONUS_MILES_FILTER_TITLE = 'Начисление миль';

export const SERP_FILTER_GROUPS: readonly SerpFilterGroup[] = [
  {
    control: 'checkbox',
    search: true,
    id: 'block',
    title: 'Туры на Emirates',
    component: EmiratesFilter,
  },
  {
    control: 'checkbox',
    id: FilterGroupId.IsExclusive,
    title: IS_EXCLUSIVE_FILTER_TITLE,
    tooltip: {
      title: IS_EXCLUSIVE_FILTER_TITLE,
      text: 'Приоритетная поддержка от Травелаты при бронировании и в туре\n90% туров с моментальным подтверждением\nТолько регулярные рейсы',
    },
  },
  {
    control: 'budget',
    id: FilterGroupId.Budget,
    title: 'Бюджет, ₽',
    component: BudgetFilter,
  },
  {
    control: 'chips',
    search: true,
    id: 'hotelCategories',
    title: 'Класс отеля',
    optionIcon: IconName.Star,
    optionIconColor: 'var(--color-brand-primary)',
    options: [
      { key: String(HotelCategoryId.FiveStars), label: '5 звёзд' },
      { key: String(HotelCategoryId.FourStars), label: '4 звезды' },
      { key: String(HotelCategoryId.ThreeStars), label: '3 звезды' },
      { key: String(HotelCategoryId.TwoStars), label: '2 звезды и меньше' },
    ],
  },
  {
    control: 'chips',
    search: true,
    id: 'meals',
    title: 'Питание',
    options: [
      { key: String(MealId.UltraAllInclusive), label: 'Ультра все включено' },
      { key: String(MealId.AllInclusive), label: 'Все включено' },
      { key: String(MealId.AllInclusiveNoAlcohol), label: 'Всё включено без алкоголя' },
      { key: String(MealId.Breakfast), label: 'Только завтраки' },
      { key: String(MealId.BreakfastAndLunch), label: 'Завтраки и обеды' },
      { key: String(MealId.FullBoard), label: 'Завтрак, обед и ужин' },
      { key: String(MealId.HalfBoard), label: 'Завтраки и ужины' },
      { key: String(MealId.RoomOnly), label: 'Без питания' },
    ],
  },
  {
    control: 'chips',
    id: FilterGroupId.BeachLine,
    title: 'Линия пляжа',
    options: [
      { key: BeachLine.First, label: '1-я линия (до 200 метров)' },
      { key: BeachLine.Second, label: '2-я линия (200-500 метров)' },
      { key: BeachLine.Third, label: '3-я линия (500-800 метров)' },
    ],
  },
  {
    control: 'chips',
    id: FilterGroupId.BeachType,
    title: 'Тип пляжа',
    options: [
      { key: BeachType.SandBeach, label: 'Песчаный' },
      { key: BeachType.PebbleBeach, label: 'Галечный' },
      { key: BeachType.SandPebbleBeach, label: 'Песчано-галечный' },
    ],
  },
  {
    control: 'checkbox',
    id: FilterGroupId.BeachOwner,
    title: 'Собственный пляж отеля',
  },
  {
    control: 'checkbox',
    search: true,
    id: 'directFlight',
    title: 'Прямые перелеты',
  },
  {
    control: 'checkbox',
    id: FilterGroupId.BonusMiles,
    title: BONUS_MILES_FILTER_TITLE,
    appendIcon: IconName.PlaneFilled,
    tooltip: {
      title: BONUS_MILES_FILTER_TITLE,
      text: 'При покупке тура для участников бонусных программ авиакомпаний возможно начисление миль. Опция доступна только при выборе туроператора Travelata',
    },
  },
  {
    control: 'checkbox',
    id: FilterGroupId.NearTheCenter,
    title: 'Близко к центру',
  },
  {
    control: 'radio',
    id: FilterGroupId.Rating,
    title: 'Оценка по отзывам',
    component: RatingFilter,
    resetLabel: 'Все оценки',
    options: [
      { key: 'best', label: 'Превосходно и выше' },
      { key: 'better', label: 'Очень хорошо и выше' },
      { key: 'good', label: 'Хорошо и выше' },
    ],
  },
  {
    control: 'chips',
    id: FilterGroupId.Prepayments,
    title: 'С предоплатой',
  },
  {
    control: 'chips',
    id: FilterGroupId.Facilities,
    title: 'Удобства',
    options: [
      { key: HotelFacility.LonelyMan, label: 'Отель принимает одиноких мужчин' },
      { key: HotelFacility.AirConditioner, label: 'Кондиционер' },
      { key: HotelFacility.Wifi, label: 'WI-FI (интернет)' },
      { key: HotelFacility.SpaAndHealth, label: 'Спа и оздоровительный центр' },
      { key: HotelFacility.BayLocated, label: 'Отель расположен в бухте' },
      { key: HotelFacility.AdultsOnly, label: 'Только для взрослых' },
      { key: HotelFacility.WithKids, label: 'Отдых с детьми' },
      { key: HotelFacility.ForCouples, label: 'Идеально для пар' },
      { key: HotelFacility.WithAnimals, label: 'Отдых с животными' },
      { key: HotelFacility.Parking, label: 'Парковка' },
      { key: HotelFacility.SaunaBathHammam, label: 'Сауна, баня, хаммам' },
      { key: HotelFacility.Jacuzzi, label: 'Джакузи' },
    ],
  },
  {
    control: 'chips',
    id: FilterGroupId.WaterActivities,
    title: 'Водные развлечения',
    options: [
      { key: WaterActivity.OpenPool, label: 'Открытый бассейн' },
      { key: WaterActivity.IndoorPool, label: 'Крытый бассейн' },
      { key: ChildService.Pool, label: 'Детский бассейн' },
      { key: WaterActivity.HeatedPool, label: 'Бассейн с подогревом' },
      { key: WaterActivity.Aquapark, label: 'Аквапарк' },
      { key: WaterActivity.SeaWaterPool, label: 'Бассейн с морской водой' },
      { key: WaterActivity.WaterSlides, label: 'Водные горки' },
    ],
  },
  {
    control: 'chips',
    id: FilterGroupId.ChildServices,
    title: 'Услуги для детей',
    options: [
      { key: ChildService.Animators, label: 'Детская анимация' },
      { key: ChildService.SeaEntry, label: 'Удобный вход в море' },
      { key: ChildService.Menu, label: 'Детское меню' },
      { key: ChildService.Bed, label: 'Детские кроватки' },
      { key: ChildService.Nanny, label: 'Услуги няни (платно)' },
      { key: ChildService.Clubs, label: 'Мини-клубы для детей' },
      { key: ChildService.Playroom, label: 'Игровая комната' },
    ],
  },
  {
    control: 'chips',
    id: FilterGroupId.Renovation,
    title: 'Открытие и реновация',
    options: [
      { key: RenovationType.RecentlyOpened, label: 'Недавно открылись' },
      { key: RenovationType.FreshRenovation, label: 'После реновации' },
    ],
  },
  {
    control: 'chips',
    id: FilterGroupId.DistanceLift,
    title: 'Расстояние до подъемника',
    options: [
      { key: DistanceLift.SkiIn, label: 'У подножия (ski-in)' },
      { key: DistanceLift.Walking, label: 'Пешая доступность (до 500 метров)' },
      { key: DistanceLift.Bus, label: 'На автобусе (от 500 метров)' },
    ],
  },
  {
    control: 'radio',
    id: FilterGroupId.RestType,
    title: 'Тип отдыха',
    resetLabel: 'Любой',
    options: [
      { key: RestType.Beach, label: 'Пляжный', description: 'Близко к пляжу или есть трансфер' },
      { key: RestType.City, label: 'Городской', description: 'В крупных городах или близко к городу' },
      { key: RestType.Mountain, label: 'Горный, горнолыжный', description: 'Близко к подъемнику или есть трансфер' },
      {
        key: RestType.Wellness,
        label: 'Оздоровительный',
        description: 'С особым питанием, врачам и медицинскими услугами',
      },
      { key: RestType.Eco, label: 'Загородный, экологичный', description: 'На природе в лесу или у озера' },
    ],
  },
  {
    control: 'checkboxes',
    search: true,
    id: 'resorts',
    title: 'Курорты',
    component: ResortsFilter,
  },
  {
    control: 'checkboxes',
    id: FilterGroupId.Operators,
    title: 'Туроператоры',
    component: OperatorsFilter,
  },
];

export const SERP_QUICK_FILTERS: readonly SerpQuickFilter[] = [
  { id: FilterGroupId.Budget, title: 'Бюджет' },
  { id: 'hotelCategories' },
  { id: 'meals' },
  { id: FilterGroupId.BeachLine },
  { id: 'directFlight' },
];
